'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  Book, 
  Search, 
  ArrowRight, 
  Rocket, 
  Shield, 
  Zap, 
  Code, 
  Database, 
  FileText, 
  HelpCircle, 
  ChevronRight,
  Terminal,
  Settings,
  Users,
  Building2,
  FolderPlus,
  Upload,
  Scan,
  CheckCircle,
  Globe,
  Lock,
  AlertTriangle,
  Lightbulb,
  ExternalLink
} from 'lucide-react';

// Documentation sections data
const quickStartSteps = [
  {
    step: 1,
    title: 'Create an Account',
    description: 'Sign up for free and verify your email to get started.',
    icon: Users,
    code: null,
  },
  {
    step: 2,
    title: 'Create an Organization',
    description: 'Set up your organization with industry sector and country.',
    icon: Building2,
    code: null,
  },
  {
    step: 3,
    title: 'Create a Project',
    description: 'Create a project to organize your digital assets.',
    icon: FolderPlus,
    code: null,
  },
  {
    step: 4,
    title: 'Add Digital Assets',
    description: 'Add your dependencies, services, and vendors as assets.',
    icon: Upload,
    code: null,
  },
  {
    step: 5,
    title: 'Run Compliance Scan',
    description: 'Execute a scan to assess compliance across 9 frameworks.',
    icon: Scan,
    code: null,
  },
];

const docCategories = [
  {
    title: 'Getting Started',
    icon: Rocket,
    color: 'bg-green-100 text-green-600',
    items: [
      { title: 'Quick Start Guide', href: '#quick-start', description: 'Get up and running in 5 minutes' },
      { title: 'Account Setup', href: '#account-setup', description: 'Create and configure your account' },
      { title: 'First Scan', href: '#first-scan', description: 'Run your first compliance scan' },
    ],
  },
  {
    title: 'Compliance Frameworks',
    icon: Shield,
    color: 'bg-blue-100 text-blue-600',
    items: [
      { title: 'GDPR Guide', href: '/docs/compliance-guide#gdpr', description: 'General Data Protection Regulation' },
      { title: 'NIS2 Guide', href: '/docs/compliance-guide#nis2', description: 'Network and Information Security' },
      { title: 'DORA Guide', href: '/docs/compliance-guide#dora', description: 'Digital Operational Resilience' },
      { title: 'All Frameworks', href: '/docs/compliance-guide', description: 'Complete compliance reference' },
    ],
  },
  {
    title: 'Features',
    icon: Zap,
    color: 'bg-purple-100 text-purple-600',
    items: [
      { title: 'Dependency Scanning', href: '#dependency-scanning', description: 'Analyze your project dependencies' },
      { title: 'Risk Assessment', href: '#risk-assessment', description: 'Understand your compliance risks' },
      { title: 'EU Alternatives', href: '#eu-alternatives', description: 'Find EU-based service alternatives' },
      { title: 'Reports & Export', href: '#reports', description: 'Generate compliance reports' },
    ],
  },
  {
    title: 'API Reference',
    icon: Code,
    color: 'bg-amber-100 text-amber-600',
    items: [
      { title: 'API Overview', href: '/api-docs', description: 'Full API documentation' },
      { title: 'Authentication', href: '/api-docs#authentication', description: 'API keys and OAuth' },
      { title: 'Endpoints', href: '/api-docs#endpoints', description: 'All available endpoints' },
      { title: 'CI/CD Integration', href: '/api-docs#cicd', description: 'GitHub Actions, GitLab CI' },
    ],
  },
];

const frameworks = [
  { name: 'GDPR', description: 'Data Protection', color: 'bg-blue-500' },
  { name: 'NIS2', description: 'Cybersecurity', color: 'bg-green-500' },
  { name: 'DORA', description: 'Digital Resilience', color: 'bg-purple-500' },
  { name: 'EU AI Act', description: 'AI Regulation', color: 'bg-pink-500' },
  { name: 'ePrivacy', description: 'Electronic Privacy', color: 'bg-amber-500' },
  { name: 'Data Act', description: 'Data Sharing', color: 'bg-cyan-500' },
  { name: 'CRA', description: 'Cyber Resilience', color: 'bg-red-500' },
  { name: 'EUCS', description: 'Cloud Security', color: 'bg-indigo-500' },
  { name: 'DSA', description: 'Digital Services', color: 'bg-teal-500' },
];

const faqs = [
  {
    question: 'What file formats can Dependra scan?',
    answer: 'Dependra can scan package.json (npm/Node.js), pom.xml (Maven/Java), requirements.txt (Python), Gemfile (Ruby), go.mod (Go), and Cargo.toml (Rust). We\'re constantly adding support for more formats.',
  },
  {
    question: 'How accurate are the compliance assessments?',
    answer: 'Our assessments are based on official regulatory documentation and industry best practices. However, they are for informational purposes and should be validated by legal/compliance professionals.',
  },
  {
    question: 'Can I integrate Dependra into my CI/CD pipeline?',
    answer: 'Yes! Enterprise plans include API access that allows you to integrate compliance scanning into your build pipeline. Contact sales for details.',
  },
  {
    question: 'How often should I run compliance scans?',
    answer: 'We recommend running scans after any dependency update, at minimum monthly. Enterprise users can configure automated scheduled scans.',
  },
  {
    question: 'What happens to my data during a scan?',
    answer: 'We only analyze your dependency manifest files (not source code). All data is processed and stored exclusively in EU data centers and encrypted at rest.',
  },
];

export default function DocsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center mb-4">
            <Book className="h-8 w-8 mr-3" />
            <span className="text-gray-400 text-sm font-medium">Documentation</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Dependra Documentation</h1>
          <p className="text-xl text-gray-300 max-w-3xl mb-8">
            Everything you need to scan dependencies, assess compliance risks, and discover EU alternatives.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
            />
          </div>
        </div>
      </div>

      {/* Quick Links Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-8 py-4 overflow-x-auto">
            <a href="#quick-start" className="text-sm font-medium text-gray-600 hover:text-blue-600 whitespace-nowrap">Quick Start</a>
            <a href="#frameworks" className="text-sm font-medium text-gray-600 hover:text-blue-600 whitespace-nowrap">Frameworks</a>
            <a href="#features" className="text-sm font-medium text-gray-600 hover:text-blue-600 whitespace-nowrap">Features</a>
            <a href="#api" className="text-sm font-medium text-gray-600 hover:text-blue-600 whitespace-nowrap">API Reference</a>
            <a href="#faq" className="text-sm font-medium text-gray-600 hover:text-blue-600 whitespace-nowrap">FAQ</a>
            <Link href="/docs/compliance-guide" className="text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap flex items-center">
              Compliance Guide <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Documentation Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {docCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
              <div className="p-6">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${category.color} mb-4`}>
                  <category.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{category.title}</h3>
                <ul className="space-y-3">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      <Link
                        href={item.href}
                        className="group flex items-start hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg transition-colors"
                      >
                        <ChevronRight className="h-4 w-4 text-gray-400 mt-0.5 mr-2 group-hover:text-blue-600" />
                        <div>
                          <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">{item.title}</span>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Start Section */}
        <section id="quick-start" className="mb-20 scroll-mt-20">
          <div className="flex items-center mb-8">
            <div className="bg-green-100 rounded-xl p-3 mr-4">
              <Rocket className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Quick Start Guide</h2>
              <p className="text-gray-600">Get up and running in 5 minutes</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="space-y-8">
              {quickStartSteps.map((step, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <step.icon className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-3">{step.description}</p>
                    {index < quickStartSteps.length - 1 && (
                      <div className="border-l-2 border-dashed border-gray-200 h-8 ml-5" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="flex items-start">
                  <Lightbulb className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Pro Tip</h4>
                    <p className="text-gray-700 text-sm">
                      You can also upload a <code className="bg-blue-100 px-2 py-0.5 rounded text-blue-700">package.json</code>, 
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-blue-700 ml-1">pom.xml</code>, or 
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-blue-700 ml-1">requirements.txt</code> file 
                      to automatically import all dependencies as assets.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Frameworks Section */}
        <section id="frameworks" className="mb-20 scroll-mt-20">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-xl p-3 mr-4">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Compliance Frameworks</h2>
                <p className="text-gray-600">9 EU regulations covered</p>
              </div>
            </div>
            <Link
              href="/docs/compliance-guide"
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
            >
              View Full Guide <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-9 gap-4 mb-8">
            {frameworks.map((framework, index) => (
              <Link
                key={index}
                href={`/docs/compliance-guide#${framework.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white rounded-xl p-4 text-center border border-gray-100 hover:border-blue-300 hover:shadow-lg transition-all group"
              >
                <div className={`w-10 h-10 ${framework.color} rounded-lg mx-auto mb-2 flex items-center justify-center`}>
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div className="font-semibold text-gray-900 text-sm group-hover:text-blue-600">{framework.name}</div>
                <div className="text-xs text-gray-500">{framework.description}</div>
              </Link>
            ))}
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Comprehensive Compliance Coverage</h3>
                <p className="text-blue-100 mb-6">
                  Our compliance guide provides detailed information on each framework, including key requirements, 
                  what Dependra checks, and step-by-step implementation guidance.
                </p>
                <Link
                  href="/docs/compliance-guide"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  Read Compliance Guide
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold">9</div>
                  <div className="text-sm text-blue-200">EU Frameworks</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold">50+</div>
                  <div className="text-sm text-blue-200">Requirements Checked</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold">100%</div>
                  <div className="text-sm text-blue-200">EU Data Storage</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold">24/7</div>
                  <div className="text-sm text-blue-200">Automated Scans</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="mb-20 scroll-mt-20">
          <div className="flex items-center mb-8">
            <div className="bg-purple-100 rounded-xl p-3 mr-4">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Features</h2>
              <p className="text-gray-600">What Dependra can do for you</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Dependency Scanning */}
            <div id="dependency-scanning" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 scroll-mt-20">
              <div className="flex items-center mb-4">
                <Database className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">Dependency Scanning</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Automatically analyze your project's dependencies to identify potential compliance risks and security vulnerabilities.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Supported Formats:</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {['package.json', 'pom.xml', 'requirements.txt', 'Gemfile', 'go.mod', 'Cargo.toml'].map((format) => (
                  <span key={format} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 font-mono">
                    {format}
                  </span>
                ))}
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">What We Check:</h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Data location and jurisdiction</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Vendor compliance certifications</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Known security vulnerabilities</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />License compatibility</li>
                </ul>
              </div>
            </div>

            {/* Risk Assessment */}
            <div id="risk-assessment" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 scroll-mt-20">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">Risk Assessment</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Get comprehensive risk scores across 9 EU compliance frameworks with actionable recommendations.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Risk Categories:</h4>
              <div className="space-y-2 mb-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2" />
                  <span className="text-sm text-gray-700">Critical - Immediate action required</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2" />
                  <span className="text-sm text-gray-700">High - Action needed soon</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2" />
                  <span className="text-sm text-gray-700">Medium - Monitor and plan</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2" />
                  <span className="text-sm text-gray-700">Low - Good standing</span>
                </div>
              </div>
            </div>

            {/* EU Alternatives */}
            <div id="eu-alternatives" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 scroll-mt-20">
              <div className="flex items-center mb-4">
                <Globe className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">EU Alternatives Directory</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Discover EU-based alternatives for non-compliant services with detailed comparisons and migration guides.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Categories:</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Cloud Infrastructure', 'Analytics', 'Authentication', 'Email', 'Payments', 'Storage', 'CDN', 'Monitoring'].map((cat) => (
                  <span key={cat} className="px-3 py-1 bg-blue-100 rounded-full text-sm text-blue-700">
                    {cat}
                  </span>
                ))}
              </div>
              <Link href="/alternatives" className="text-blue-600 hover:text-blue-700 font-medium flex items-center text-sm">
                Browse 500+ EU Alternatives <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            {/* Reports */}
            <div id="reports" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 scroll-mt-20">
              <div className="flex items-center mb-4">
                <FileText className="h-6 w-6 text-green-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">Reports & Export</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Generate detailed compliance reports for audits, stakeholders, and regulatory submissions.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Report Types:</h4>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Executive Summary (PDF)
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Detailed Compliance Report (PDF)
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Data Export (JSON/CSV)
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  API Integration (Enterprise)
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* API Reference Section */}
        <section id="api" className="mb-20 scroll-mt-20">
          <div className="flex items-center mb-8">
            <div className="bg-amber-100 rounded-xl p-3 mr-4">
              <Code className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">API Reference</h2>
              <p className="text-gray-600">Integrate Dependra into your workflow</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Programmatic Access</h3>
                <p className="text-amber-100 mb-6">
                  Use our REST API to scan dependencies, check compliance, and discover EU alternatives 
                  directly from your CI/CD pipeline or custom applications.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/api-docs"
                    className="inline-flex items-center px-6 py-3 bg-white text-amber-600 rounded-xl font-semibold hover:bg-amber-50 transition-colors"
                  >
                    <Code className="h-5 w-5 mr-2" />
                    View API Docs
                  </Link>
                  <Link
                    href="/pricing"
                    className="inline-flex items-center px-6 py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors border border-white/20"
                  >
                    API Pricing
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Terminal className="h-6 w-6 text-amber-200 mb-2" />
                  <div className="font-semibold">REST API</div>
                  <div className="text-sm text-amber-200">JSON responses</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Lock className="h-6 w-6 text-amber-200 mb-2" />
                  <div className="font-semibold">OAuth 2.0</div>
                  <div className="text-sm text-amber-200">Secure auth</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Settings className="h-6 w-6 text-amber-200 mb-2" />
                  <div className="font-semibold">Webhooks</div>
                  <div className="text-sm text-amber-200">Real-time events</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Zap className="h-6 w-6 text-amber-200 mb-2" />
                  <div className="font-semibold">CI/CD Ready</div>
                  <div className="text-sm text-amber-200">Easy integration</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="mb-20 scroll-mt-20">
          <div className="flex items-center mb-8">
            <div className="bg-indigo-100 rounded-xl p-3 mr-4">
              <HelpCircle className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
              <p className="text-gray-600">Common questions about Dependra</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-100 last:border-0">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Help Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl font-bold mb-4">Need More Help?</h2>
                <p className="text-gray-300 mb-6">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Contact Support
                  </Link>
                  <Link
                    href="/docs/compliance-guide"
                    className="inline-flex items-center px-6 py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors border border-white/20"
                  >
                    Compliance Guide
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <HelpCircle className="h-6 w-6 text-blue-400 mb-2" />
                  <div className="font-semibold">Knowledge Base</div>
                  <div className="text-sm text-gray-400">Self-service resources</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Users className="h-6 w-6 text-green-400 mb-2" />
                  <div className="font-semibold">Community</div>
                  <div className="text-sm text-gray-400">Join discussions</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
